import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import UpsertMedicamentForm from 'components/medicaments/UpsertMedicamentForm';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Modal } from 'semantic-ui-react';
import {
  deleteMedicament,
  findMedicamentById,
  getAllTariffCodes,
  searchMedicaments
} from 'services/medicamentServices';
import { debounce } from 'ts-debounce';
import { MedicamentDto, MedicamentEnumDto, MedicamentSearchRequest, UpsertMedicamentDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const initialMedicamentSearch: Partial<MedicamentSearchRequest> = {};

interface MedicamentsFieldProps extends FormState {
  values: Partial<MedicamentSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundMedicaments: Array<MedicamentDto>,
  medicamentsLoaded: boolean,
  medicament?: UpsertMedicamentDto,
  medicamentId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  tariffCodes?: Array<MedicamentEnumDto>,
  selectedTariffCode?: string | undefined,
  searchValue: Partial<MedicamentSearchRequest>,
  cancelTokenSource: CancelTokenSource,
}

class MedicamentsView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialSearchFormValues = {
    tariffCode: 402
  };

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundMedicaments: [],
      medicamentsLoaded: false,
      medicament: undefined,
      medicamentId: undefined,
      addEditModalOpen: false,
      loading: false,
      tariffCodes: [],
      selectedTariffCode: undefined,
      searchValue: initialMedicamentSearch,
      cancelTokenSource
    };

    getAllTariffCodes(cancelTokenSource)
        .then(response => {
          response.unshift({code: "0", name: `unknown`});
          this.setState({
            tariffCodes: response
          })
        })
        .catch(noop);
  }


  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findMedicamentById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, medicament?: UpsertMedicamentDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        medicament: medicament,
        medicamentId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      medicament: undefined,
      medicamentId: undefined
    }));
  };

  saveUpsertForm = () => {
    const {searchValue, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        medicamentsLoaded: true
      })
    };

    searchMedicaments(searchValue, cancelTokenSource)
        .then(response => {
          this.setState({
            foundMedicaments: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteMedicament = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteMedicament(id, cancelTokenSource).then(response => {
      this.setState(({foundMedicaments}: State) => ({
        foundMedicaments: foundMedicaments.filter((medicament: MedicamentDto) => medicament.id != response.id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: MedicamentsFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;

    this.setState({
      medicamentsLoaded: false
    });

    values.selectedOnly = true;

    if (values.tariffCode == "0") {
      values.tariffCode = undefined;
    }

    const onFinally = () => {
      this.setState({
        medicamentsLoaded: true
      })
    };

    searchMedicaments(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundMedicaments: response,
              selectedTariffCode: values.tariffCode,
              searchValue: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {medicamentsLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          {this.renderSearchMedicamentSegment()}

          <StyledViewTableRow>
            {!medicamentsLoaded
                ? <LoaderComponent message={getMessage("medicament.loading")}/>
                : this.renderMedicamentsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertMedicamentModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchMedicamentSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialSearchFormValues}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;

    const {tariffCodes, foundMedicaments} = this.state;

    const numResults = foundMedicaments.length;

    const tariffCodeOptions = tariffCodes ? tariffCodes.map(tariffCode => {
      if (tariffCode.code == "0") {
        return {
          key: parseInt(tariffCode.code),
          text: `${getMessage(`tc.${tariffCode.name}`)}`,
          value: parseInt(tariffCode.code)
        }
      }
      return {
        key: parseInt(tariffCode.code),
        text: `${tariffCode.code}, ${getMessage(`tc.${tariffCode.name}`)}`,
        value: parseInt(tariffCode.code)
      }
    }) : [{key: 400, value: 400, text: `${getMessage(`tc.medicine`)}`}];

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={2} verticalAlign="middle">
              {getMessage("tariff")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tariffCode"
                  component={Select}
                  fluid
                  options={tariffCodeOptions}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={2} verticalAlign="middle">
              {`${getMessage("eanCode")} / ${getMessage("pharmaCode")} / ${getMessage("description")}`}:
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  name="query"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  actionsCell = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("medicament.confirmDelete")}
              onConfirm={() => this.deleteMedicament(cellData)}
          />
        </React.Fragment>
    );

  };

  rowGetter = ({index}: any) => {
    const {foundMedicaments} = this.state;

    Object.assign(foundMedicaments[index], {index: index + 1});

    return foundMedicaments[index];
  };

  foundMedicamentsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundMedicaments} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundMedicaments[index].id)}
        >
          {columns}
        </div>
    );
  };


  renderMedicamentsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundMedicaments} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundMedicaments.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.foundMedicamentsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 70,
                flexGrow: 0,
                label: "#",
                dataKey: "index"
              },
              {
                width: 110,
                flexGrow: 0,
                label: (getMessage("code")),
                dataKey: "code"
              },
              {
                width: 110,
                flexGrow: 0,
                label: (getMessage("pharmaCode")),
                dataKey: "pharmaCode"
              },
              {
                width: 130,
                flexGrow: 0,
                label: (getMessage("eanCode")),
                dataKey: "eanCode"
              },
              {
                width: 300,
                flexGrow: 1,
                flexShrink: 0,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 100,
                flexGrow: 0,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCell)
              }
            ]}
        />
    );
  };

  renderUpsertMedicamentModal = (): React.ReactNode => {

    const {getMessage} = this.props;

    const {
      addEditModalOpen,
      medicament,
      medicamentId,
      selectedTariffCode
    } = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("medicaments")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertMedicamentForm
                medicament={medicament}
                medicamentId={medicamentId}
                tariffCode={selectedTariffCode}
                onCancel={this.hideModal}
                onSave={this.saveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

}

export default withMessages(MedicamentsView);
